<template>
    <div class="line-chart-container">
        <div ref="chartContainer" class="chart-container"></div>
    </div>
</template>

<script>
import * as echarts from "echarts";
import { debounce } from "lodash";
import _ from "lodash";

export default {
    name: "LineChart",
    props: {
        data: {
            type: Array,
            required: true,
        },
        settings: {
            type: Object,
            required: true,
        },
        title: {
            type: String,
            required: true,
        },
        fields: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            chart: null,
            resizeObserver: null,
        };
    },
    computed: {
        xAxisField() {
            return this.fields.find((f) => f._id === this.settings.xAxis.field);
        },
        yAxisField() {
            if (this.settings.yAxis.calculationType === "count") return null;
            return this.fields.find((f) => f._id === this.settings.yAxis.field);
        },
        stackByField() {
            if (!this.settings.stackBy || !this.settings.stackBy.field) return null;
            return this.fields.find((f) => f._id === this.settings.stackBy.field);
        },
        isStacked() {
            return this.settings.chartType.includes("stacked");
        },
        isSmooth() {
            return this.settings.chartType === "smooth-line";
        },
        $_() {
            return _;
        },
    },
    watch: {
        data: {
            handler() {
                this.updateChart();
            },
            deep: true,
        },
        settings: {
            handler() {
                this.updateChart();
            },
            deep: true,
        },
    },
    methods: {
        initChart() {
            if (this.chart) {
                this.chart.dispose();
            }

            // Ensure container exists and has dimensions
            if (
                !this.$refs.chartContainer ||
                !this.$refs.chartContainer.offsetWidth ||
                !this.$refs.chartContainer.offsetHeight
            ) {
                return;
            }

            this.chart = echarts.init(this.$refs.chartContainer);
            this.updateChart();
            this.initResizeObserver();
        },
        initResizeObserver() {
            if (this.resizeObserver) {
                this.resizeObserver.disconnect();
            }

            this.resizeObserver = new ResizeObserver(
                _.debounce(() => {
                    if (this.chart && this.$refs.chartContainer) {
                        requestAnimationFrame(() => {
                            this.chart.resize();
                        });
                    }
                }, 100)
            );

            if (this.$refs.chartContainer) {
                this.resizeObserver.observe(this.$refs.chartContainer);
            }
        },
        updateChart() {
            if (!this.chart || !this.data || !this.data.length) return;

            const xAxisField = this.fields.find((f) => f._id === this.settings.xAxis.field);
            if (!xAxisField) return;

            const xAxisData = this.data.map((item) => item[xAxisField.name]);
            let series = [];

            if (this.isStacked && this.stackByField) {
                const stackGroups = new Set();
                this.data.forEach((item) => {
                    Object.keys(item).forEach((key) => {
                        if (key !== xAxisField.name) stackGroups.add(key);
                    });
                });

                series = Array.from(stackGroups).map((group) => {
                    const groupData = this.data.map((item) => item[group] || 0);
                    const customLabel = this.$_.get(this.settings, `customization.labelCustomization.${group}`);

                    return {
                        name: customLabel?.label || group,
                        type: "line",
                        stack: "total",
                        smooth: this.isSmooth,
                        areaStyle: this.settings.chartType.includes("area") ? {} : null,
                        emphasis: { focus: "series" },
                        data: groupData,
                        lineStyle: {
                            color: customLabel?.color,
                            width: 2,
                        },
                        itemStyle: {
                            color: customLabel?.color,
                        },
                    };
                });
            } else {
                const yAxisLabel = this.yAxisField 
                    ? `${this.yAxisField.name} (${this.settings.yAxis.calculationType})`
                    : 'Kayıt Sayısı';
                const defaultCustomization = this.$_.get(this.settings, 'customization.labelCustomization.default');
                const defaultColor = defaultCustomization?.color || '#1890ff';
                const defaultLabel = defaultCustomization?.label || yAxisLabel;

                series = [
                    {
                        name: defaultLabel,
                        type: "line",
                        smooth: this.isSmooth,
                        data: this.data.map((item) => item.value),
                        lineStyle: {
                            color: defaultColor,
                            width: 2,
                        },
                        itemStyle: {
                            color: defaultColor,
                        },
                        areaStyle: this.settings.chartType.includes("area") ? {
                            color: defaultColor,
                            opacity: 0.3
                        } : null,
                    },
                ];
            }

            const markLines = [];
            const benchmarkLines = this.settings?.customization?.benchmarkLines || this.settings?.benchmarkLines || [];
            if (benchmarkLines.length) {
                benchmarkLines.forEach((line) => {
                    if (line.axis === "y") {
                        markLines.push({
                            name: line.label,
                            yAxis: line.value,
                            label: {
                                show: true,
                                formatter: line.text || line.label,
                                position: "end",
                            },

                            data: {
                                name: line.label,
                                yAxis: line.value,
                                lineStyle: {
                                    color: line.color,
                                    type: "dashed",
                                    width: 2,
                                },
                                name: line.label,
                                yAxis: line.value,
                                label: {
                                    formatter: `${line.label}${line.text ? ": " + line.text : ""}`,
                                    position: "middle",
                                    show: true,
                                    color: line.color,
                                    fontSize: 12,
                                    backgroundColor: "#fff",
                                    padding: [4, 8],
                                },
                            },
                        });
                    }
                    if (line.axis === "x") {
                        markLines.push({
                            name: line.label,
                            xAxis: line.value,
                            data: {
                                name: line.label,
                                xAxis: line.value,
                                lineStyle: {
                                    color: line.color,
                                    type: "dashed",
                                    width: 2,
                                },
                                label: {
                                    formatter: `${line.label}${line.text ? ": " + line.text : ""}`,
                                    position: "middle",
                                    show: true,
                                    color: line.color,
                                    fontSize: 12,
                                    backgroundColor: "#fff",
                                    padding: [4, 8],
                                },
                            },
                        });
                    }
                });
            }

            const option = {
                title: {
                    text: this.title,
                    left: "center",
                },
                tooltip: {
                    trigger: "axis",
                    axisPointer: {
                        type: "cross",
                        label: {
                            backgroundColor: "#6a7985",
                        },
                    },
                },
                legend:
                    series.length > 1
                        ? {
                              data: series.map((s) => s.name),
                              top: 30,
                          }
                        : false,
                grid: {
                    left: "3%",
                    right: "4%",
                    bottom: "3%",
                    containLabel: true,
                },
                xAxis: {
                    type: "category",
                    boundaryGap: false,
                    data: xAxisData,
                    name: xAxisField?.name,
                },
                yAxis: {
                    type: "value",
                    name: this.yAxisField?.name || "Değer",
                },
                series: series.map((s, i) => ({
                    ...s,
                    markLine: markLines.length
                        ? {
                              symbol: ["none", "none"],
                              silent: true,
                              data: markLines.map((x) => x.data),
                          }
                        : undefined,
                })),
            };
            console.log("chart option", {
                option,
                benchmarkLines: this.settings.customization?.benchmarkLines,
                markLines,
                settings: this.$_.cloneDeep(this.settings),
            });

            this.chart.setOption(option, true);
        },
    },
    mounted() {
        // Wait for container to be rendered
        this.$nextTick(() => {
            requestAnimationFrame(() => {
                this.initChart();
            });
        });
    },
    beforeDestroy() {
        if (this.chart) {
            this.chart.dispose();
            this.chart = null;
        }
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    },
};
</script>

<style lang="scss" scoped>
.line-chart-container {
    width: 100%;
    height: 100%;
    min-height: 200px;
    position: relative;

    .chart-container {
        width: 100%;
        height: 100%;
        min-height: 200px;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
    }
}
</style>
